import React, { useState } from 'react';
import { useShop } from '../context/ShopContext';
import ProductCard from './ProductCard';
import { formatINR } from '../data/products';
import { SlidersHorizontal, RotateCcw, Search, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';

export default function ProductCatalog() {
  const {
    products,
    categories,
    filteredProducts,
    activeCategory,
    setActiveCategory,
    searchQuery,
    setSearchQuery,
    selectedMetal,
    setSelectedMetal,
    maxPrice,
    setMaxPrice,
    sortBy,
    setSortBy
  } = useShop();

  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const resetFilters = () => {
    setActiveCategory('all');
    setSearchQuery('');
    setSelectedMetal('all');
    setMaxPrice(450000);
    setSortBy('featured');
  };

  const safeProducts = Array.isArray(products) ? products : [];
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeFilteredProducts = Array.isArray(filteredProducts) ? filteredProducts : [];

  const isFiltered = activeCategory !== 'all' || searchQuery !== '' || selectedMetal !== 'all' || maxPrice < 450000;

  return (
    <section id="catalog-section" style={{ padding: '16px 0 36px', backgroundColor: '#f1f3f6' }}>
      <div className="container">
        
        {/* Flipkart White Card Section Header */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '2px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
          marginBottom: '12px',
          overflow: 'hidden'
        }}>
          {/* Top Title Bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #f0f0f0',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div>
              <h2 style={{
                fontSize: '1.35rem',
                fontWeight: 700,
                color: '#212121',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                {activeCategory === 'all' 
                  ? 'Deals of the Day • Top Jewellery Offers' 
                  : (safeCategories.find(c => c.id === activeCategory)?.name || 'Featured Creations')}
                <span style={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: '#388e3c',
                  backgroundColor: '#e8f5e9',
                  padding: '2px 8px',
                  borderRadius: '2px'
                }}>
                  {safeFilteredProducts.length} Items Available
                </span>
              </h2>
            </div>

            {/* Quick Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isFiltered && (
                <button
                  onClick={resetFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2874f0',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <RotateCcw size={13} /> Clear Filters
                </button>
              )}

              <button
                className="btn btn-outline btn-sm mobile-only"
                onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                style={{
                  padding: '6px 12px',
                  fontSize: '0.8rem',
                  color: '#2874f0',
                  border: '1px solid #2874f0',
                  borderRadius: '2px',
                  background: '#fff'
                }}
              >
                <SlidersHorizontal size={14} />
                Filters
                {mobileFilterOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>

              <button
                onClick={() => { setActiveCategory('all'); }}
                style={{
                  backgroundColor: '#2874f0',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '2px',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  padding: '8px 20px',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  boxShadow: '0 2px 4px 0 rgba(0,0,0,.15)'
                }}
              >
                VIEW ALL
              </button>
            </div>
          </div>

          {/* Flipkart Category Filter Pills (Horizontal Scroll) */}
          <div 
            className="hide-scrollbar"
            style={{
              display: 'flex',
              gap: '6px',
              overflowX: 'auto',
              padding: '10px 20px',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#fafafa'
            }}
          >
            {[{ id: 'all', name: 'All Jewellery', count: safeProducts.length }, ...safeCategories.map(c => ({ id: c.id, name: c.name, count: safeProducts.filter(p => p && p.category === c.id).length }))].map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  style={{
                    backgroundColor: isActive ? '#2874f0' : '#ffffff',
                    color: isActive ? '#ffffff' : '#212121',
                    border: isActive ? '1px solid #2874f0' : '1px solid #e0e0e0',
                    padding: '6px 14px',
                    borderRadius: '2px',
                    fontSize: '0.82rem',
                    fontWeight: isActive ? 600 : 500,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span>{cat.name}</span>
                  <span style={{
                    fontSize: '0.7rem',
                    opacity: isActive ? 1 : 0.7,
                    backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : '#f0f0f0',
                    color: isActive ? '#ffffff' : '#555555',
                    padding: '1px 5px',
                    borderRadius: '2px',
                    fontWeight: 700
                  }}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Flipkart Sort & Filter Options Strip */}
          <div 
            className={`filter-bar-wrapper ${mobileFilterOpen ? 'mobile-open' : ''}`}
            style={{
              padding: '12px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              flexWrap: 'wrap',
              backgroundColor: '#ffffff'
            }}
          >
            {/* Sort Tabs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.84rem', fontWeight: 600, color: '#212121' }}>Sort By:</span>
              {[
                { id: 'featured', label: 'Popularity' },
                { id: 'price-low', label: 'Price -- Low to High' },
                { id: 'price-high', label: 'Price -- High to Low' },
                { id: 'rating', label: 'Customer Rating' }
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setSortBy(opt.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    borderBottom: sortBy === opt.id ? '2px solid #2874f0' : '2px solid transparent',
                    color: sortBy === opt.id ? '#2874f0' : '#878787',
                    fontWeight: sortBy === opt.id ? 700 : 500,
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    padding: '4px 0'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Metal Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
              <span style={{ fontSize: '0.82rem', color: '#878787' }}>Metal:</span>
              <select
                value={selectedMetal}
                onChange={(e) => setSelectedMetal(e.target.value)}
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e0e0e0',
                  color: '#212121',
                  padding: '4px 10px',
                  borderRadius: '2px',
                  fontSize: '0.82rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Metals</option>
                <option value="22k">22K Gold (916)</option>
                <option value="18k">18K Gold (750)</option>
                <option value="silver">Pure Silver (925/999)</option>
              </select>

              {/* Price Range Slider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '180px' }}>
                <span style={{ fontSize: '0.82rem', color: '#878787' }}>Under:</span>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#2874f0' }}>{formatINR(maxPrice)}</span>
                <input
                  type="range"
                  min="30000"
                  max="450000"
                  step="10000"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(Number(e.target.value))}
                  style={{
                    accentColor: '#2874f0',
                    cursor: 'pointer',
                    width: '100px'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid (Pure White Flipkart Cards) */}
        {safeFilteredProducts.length > 0 ? (
          <div 
            className="catalog-products-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '10px'
            }}
          >
            {safeFilteredProducts.map((product) => (
              <ProductCard key={product.id || product.name} product={product} />
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '4rem 1.5rem',
            background: '#ffffff',
            borderRadius: '2px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
          }}>
            <Search size={44} color="#2874f0" style={{ margin: '0 auto 0.8rem', opacity: 0.8 }} />
            <h3 style={{ fontSize: '1.2rem', color: '#212121', marginBottom: '0.4rem', fontWeight: 600 }}>
              No jewellery matching your selection
            </h3>
            <p style={{ color: '#878787', fontSize: '0.88rem', marginBottom: '1.2rem' }}>
              Try adjusting your price range or metal selection to view more items.
            </p>
            <button 
              onClick={resetFilters}
              style={{
                backgroundColor: '#2874f0',
                color: '#ffffff',
                border: 'none',
                borderRadius: '2px',
                padding: '8px 24px',
                fontWeight: 600,
                fontSize: '0.88rem',
                cursor: 'pointer'
              }}
            >
              Reset All Filters
            </button>
          </div>
        )}

      </div>

      <style>{`
        @media (max-width: 768px) {
          .filter-bar-wrapper {
            display: none !important;
          }
          .filter-bar-wrapper.mobile-open {
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
        }
      `}</style>
    </section>
  );
}
